import { type ReactNode, Suspense, use, useContext } from "react";

import { ShopperDataContext, hydratingPprDocument } from "./ppr-document";
import type { ShopperData } from "./shopper-data";

/**
 * Per-shopper holes in the homepage document shell (COM-482).
 *
 * The homepage document is prerendered once from a cookieless guest request
 * and that shell is replayed for every eligible request; a hole marks the
 * parts that must come from THIS request (sign-in state, basket, per-shopper
 * analytics globals). See `docs/ppr-shell.md`.
 *
 * A hole is a `<Suspense>` boundary whose child waits for "shopper data" — a
 * promise from context. The shell prerender (`ppr.server.tsx`) provides one
 * that never settles: every hole suspends, the render is aborted once the
 * static parts are done, and React records the holes as postponed. The
 * resume of a shell provides an already-fulfilled one, so the holes render
 * inline into the postponed slots.
 *
 * Outside a shell render — a regular document, and the client hydrating a
 * regular document — a hole renders its children with NO boundary. That is
 * deliberate, not an optimisation: Fizz outlines any completed boundary once
 * more than `progressiveChunkSize` (12.8 KB) has been flushed before it
 * (`flushSegment`), and the homepage streams ~300 KB of head before the
 * first hole, so an always-on boundary would push every hole to the tail of
 * the document on every regular render — the header showing the guest
 * fallback until the end, and the per-shopper inline scripts running after
 * the Builder body (breaking the COM-101 `builder.userAttributes` order and
 * the COM-198 no-streaming invariant). The browser follows whichever shape
 * the server sent: a shell prelude carries `PPR_DOCUMENT_FLAG`.
 *
 * Everything outside a hole must render identically for every guest — the
 * client hydrates the cached shell against the live request's loader data.
 * Reading a per-shopper or per-request value outside a hole is a bug: the
 * fingerprint check (`ppr-fingerprint.server.ts`) catches the loader-data
 * kind, the shell-invariance test in `ppr-hole.test.tsx` shows the pattern.
 */

export function ShopperDataProvider({
  data,
  children,
}: {
  data: ShopperData;
  children: ReactNode;
}) {
  return <ShopperDataContext value={data}>{children}</ShopperDataContext>;
}

/**
 * A part of the document that needs this shopper's data. `name` identifies
 * the hole in the shell build log and in React DevTools; `fallback` is what
 * the cached shell shows until the hole resumes (never seen in a regular
 * document).
 */
export function PprHole({
  name,
  children,
  fallback = null,
}: {
  name: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const data = useContext(ShopperDataContext);
  if (data === null && !hydratingPprDocument) return children;
  return (
    <Suspense name={name} fallback={fallback}>
      <AwaitShopperData name={name}>{children}</AwaitShopperData>
    </Suspense>
  );
}

function AwaitShopperData({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  const data = useContext(ShopperDataContext);
  // The client hydrating a shell has no provider: its data is here already.
  if (data === null) return children;
  if (data.status === "pending") data.holes.add(name);
  use(data);
  return children;
}
